import { cva } from "class-variance-authority";
import { Link } from "react-router";
import { cn } from "../../../client/cn";
import {
  BUTTON_PRIMITIVE_DEFAULTS,
  CANONICAL_ICON_BUTTON_BASE_CLASSES,
  ICON_BUTTON_SIZE_CLASSES,
  type CanonicalButtonSize,
  type CanonicalButtonTone,
} from "./buttonPrimitiveSpec";

export interface IconButtonProps extends React.AriaAttributes {
  icon: React.ReactNode;
  onClick?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
  className?: string;
  href?: string;
  tone?: CanonicalButtonTone;
  size?: CanonicalButtonSize;
  loading?: boolean;
  disabled?: boolean;
  id?: string;
  title?: string;
  tabIndex?: number;
  role?: React.AriaRole;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
}

const iconToneClasses: Record<CanonicalButtonTone, string> = {
  primary:
    "hover:bg-primary hover:text-white dark:hover:bg-primary dark:hover:text-white focus-visible:ring-primary/30",
  secondary:
    "hover:bg-secondary hover:text-white dark:hover:bg-secondary dark:hover:text-white focus-visible:ring-secondary/30",
  success:
    "hover:bg-success hover:text-white dark:hover:bg-success dark:hover:text-white focus-visible:ring-success/30",
  danger:
    "hover:bg-danger hover:text-white dark:hover:bg-danger dark:hover:text-white focus-visible:ring-danger/30",
  warning:
    "hover:bg-warning hover:text-white dark:hover:bg-warning dark:hover:text-white focus-visible:ring-warning/30",
  info: "hover:bg-info hover:text-white dark:hover:bg-info dark:hover:text-white focus-visible:ring-info/30",
  neutral:
    "hover:bg-neutral-700 hover:text-white dark:hover:bg-neutral-200 dark:hover:text-neutral-900 focus-visible:ring-neutral/30",
};

const iconButtonVariants = cva(CANONICAL_ICON_BUTTON_BASE_CLASSES, {
  variants: {
    tone: iconToneClasses,
    size: ICON_BUTTON_SIZE_CLASSES,
    loading: {
      true: "disabled:cursor-wait disabled:opacity-70",
      false: "",
    },
  },
  defaultVariants: {
    tone: BUTTON_PRIMITIVE_DEFAULTS.tone,
    size: BUTTON_PRIMITIVE_DEFAULTS.size,
    loading: BUTTON_PRIMITIVE_DEFAULTS.loading,
  },
});

export function IconButton({
  icon,
  onClick,
  className = "",
  href,
  tone = BUTTON_PRIMITIVE_DEFAULTS.tone,
  size = BUTTON_PRIMITIVE_DEFAULTS.size,
  loading = BUTTON_PRIMITIVE_DEFAULTS.loading,
  disabled = false,
  id,
  title,
  tabIndex,
  role,
  type,
  ...ariaProps
}: IconButtonProps) {
  const isDisabled = Boolean(disabled || loading);
  const iconButtonClasses = cn(
    iconButtonVariants({ tone, size, loading }),
    className,
  );

  if (href) {
    return (
      <Link
        to={href}
        id={id}
        title={title}
        className={iconButtonClasses}
        tabIndex={isDisabled ? -1 : tabIndex}
        role={role}
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
        {...ariaProps}
        onClick={(event) => {
          event.stopPropagation();
          if (isDisabled) {
            event.preventDefault();
            return;
          }
          onClick?.(event);
        }}
      >
        {icon}
      </Link>
    );
  }

  return (
    <button
      id={id}
      title={title}
      type={type}
      tabIndex={tabIndex}
      role={role}
      className={iconButtonClasses}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      {...ariaProps}
      onClick={(event) => {
        event.stopPropagation();
        if (isDisabled) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
    >
      {icon}
    </button>
  );
}
